/**
 * The signal floor.
 *
 * This is the picture of a refusal. Not an apology, not a pulsing orb standing
 * in for a rhythm that was never found — the actual thing Ori looked at, and
 * the actual bar it failed to clear.
 *
 * Three marks carry the whole argument:
 *
 *   the band      every candidate heart rate between 42 and 240 bpm, and how
 *                 much energy the recording holds at each
 *   the rival     the tallest thing in that band that is NOT the candidate —
 *                 the noise the candidate has to beat
 *   the bar       three times the rival, which is what "prominent enough to
 *                 report" means in this product
 *
 * When the peak clears the bar, you see why Ori answered. When it does not,
 * you see why it did not, and the answer is legible without knowing what a
 * power spectrum is: nothing here is tall enough.
 *
 * Drawn on canvas at animation rate, reading straight from the session, because
 * this is a few hundred numbers changing several times a second and routing it
 * through React state would cost more than the picture is worth.
 */

import { useEffect, useRef } from 'react';
import { useI18n } from '../lib/i18n.jsx';

const PALETTE = {
  pulse: '#f87f94',
  pulseDeep: '#8e3c4b',
  signal: '#63ccab',
  warn: '#ebb673',
  muted: '#bfadb1',
  faint: '#a28f93',
  rule: '#3a2d31',
  ruleLit: '#584447',
  ink: '#fcf8f7',
};

/** Same reasoning as the rest of the canvas work: Plex Mono cannot compose
 *  Yorùbá and Igbo tone marks, and canvas has no fallback chain to rely on. */
function label(px) {
  const english = typeof document === 'undefined' || document.documentElement.lang === 'en';
  return english
    ? `${px}px "IBM Plex Mono", ui-monospace, monospace`
    : `${px + 1}px "Archivo", "Helvetica Neue", Arial, sans-serif`;
}

function fit(canvas) {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const rect = canvas.getBoundingClientRect();
  if (rect.width < 2 || rect.height < 2) return null;
  const w = Math.round(rect.width * dpr);
  const h = Math.round(rect.height * dpr);
  if (canvas.width !== w || canvas.height !== h) {
    canvas.width = w;
    canvas.height = h;
  }
  const ctx = canvas.getContext('2d');
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, rect.width, rect.height);
  return { ctx, w: rect.width, h: rect.height };
}

/**
 * @param {boolean} labelPeak print the candidate rate beside the mark. False in
 *   the patient view for a refusal: naming "229 bpm" next to a peak the app has
 *   just declined to report contradicts the refusal in the same breath. A
 *   clinician does want the number, so their view asks for it.
 */
export function SignalFloor({ getSpectrum, active, strings, labelPeak = true }) {
  const { t } = useI18n();
  const ref = useRef(null);
  const activeRef = useRef(active);
  activeRef.current = active;

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;
    let raf = 0;
    let stopped = false;

    const draw = () => {
      const f = fit(canvas);
      if (!f) return;
      const { ctx, w: W, h: H } = f;
      const view = getSpectrum();

      const PAD_L = 8;
      const PAD_R = 8;
      const PAD_T = 20;
      const PAD_B = 22;
      const plotW = W - PAD_L - PAD_R;
      const plotH = H - PAD_T - PAD_B;

      if (!view || view.bins.length < 4) {
        ctx.fillStyle = PALETTE.faint;
        ctx.font = label(12);
        ctx.fillText(t('floor.empty'), PAD_L, H / 2 + 4);
        return;
      }

      // Scale to whichever is taller — the peak or the bar it must clear — so
      // the bar is always on screen. A chart that crops the threshold when the
      // signal is weak would hide the exact case this drawing exists for.
      const top = Math.max(view.peakPower, view.thresholdPower) * 1.18 || 1;
      const y = (p) => PAD_T + (1 - Math.min(p / top, 1)) * plotH;
      const x = (bpm) =>
        PAD_L + ((bpm - view.loBpm) / Math.max(view.hiBpm - view.loBpm, 1e-6)) * plotW;

      // baseline
      ctx.strokeStyle = PALETTE.rule;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(PAD_L, PAD_T + plotH);
      ctx.lineTo(PAD_L + plotW, PAD_T + plotH);
      ctx.stroke();

      // the band itself, as a filled area — this is "everything the recording
      // could possibly have mistaken for a heartbeat"
      ctx.beginPath();
      ctx.moveTo(x(view.bins[0].bpm), PAD_T + plotH);
      for (const b of view.bins) ctx.lineTo(x(b.bpm), y(b.power));
      ctx.lineTo(x(view.bins[view.bins.length - 1].bpm), PAD_T + plotH);
      ctx.closePath();
      ctx.fillStyle = view.clears ? 'rgba(99, 204, 171, 0.16)' : 'rgba(191, 173, 177, 0.14)';
      ctx.fill();

      ctx.beginPath();
      for (let i = 0; i < view.bins.length; i++) {
        const b = view.bins[i];
        const px = x(b.bpm);
        const py = y(b.power);
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.strokeStyle = view.clears ? PALETTE.signal : PALETTE.muted;
      ctx.lineWidth = 1.4;
      ctx.lineJoin = 'round';
      ctx.stroke();

      // the rival: the tallest competing thing, drawn as a quiet solid line
      const rivalY = y(view.competitorPower);
      ctx.strokeStyle = PALETTE.ruleLit;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(PAD_L, rivalY);
      ctx.lineTo(PAD_L + plotW, rivalY);
      ctx.stroke();

      // the bar it has to clear
      const barY = y(view.thresholdPower);
      ctx.save();
      ctx.setLineDash([5, 4]);
      ctx.strokeStyle = view.clears ? PALETTE.signal : PALETTE.pulse;
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.moveTo(PAD_L, barY);
      ctx.lineTo(PAD_L + plotW, barY);
      ctx.stroke();
      ctx.restore();

      ctx.font = label(10);
      ctx.fillStyle = view.clears ? PALETTE.signal : PALETTE.pulse;
      const barLabel = t('floor.bar');
      ctx.fillText(barLabel, PAD_L + 2, Math.max(barY - 5, PAD_T - 6));

      // the candidate
      if (view.peakBpm !== null && Number.isFinite(view.peakBpm)) {
        const px = x(view.peakBpm);
        const py = y(view.peakPower);
        ctx.strokeStyle = view.clears ? PALETTE.signal : PALETTE.pulseDeep;
        ctx.lineWidth = 1;
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.moveTo(px, py);
        ctx.lineTo(px, PAD_T + plotH);
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(px, py, 3.4, 0, Math.PI * 2);
        ctx.fillStyle = view.clears ? PALETTE.signal : PALETTE.pulse;
        ctx.fill();

        if (labelPeak) {
          // Sit the text on whichever side of the mark has room, so it is
          // never clipped by the right edge and never printed over the dot.
          ctx.font = label(11);
          ctx.fillStyle = PALETTE.ink;
          const text = `${Math.round(view.peakBpm)} ${strings.bpm}`;
          const tw = ctx.measureText(text).width;
          const right = px + 9;
          const tx = right + tw <= PAD_L + plotW ? right : px - 9 - tw;
          ctx.fillText(text, Math.max(tx, PAD_L), Math.max(py - 8, PAD_T + 9));
        }
      }

      // axis ends only — a tick every 20 bpm would be noise in a 200px box
      ctx.font = label(10);
      ctx.fillStyle = PALETTE.faint;
      ctx.fillText(String(Math.round(view.loBpm)), PAD_L, H - 6);
      const hi = String(Math.round(view.hiBpm));
      ctx.fillText(hi, PAD_L + plotW - ctx.measureText(hi).width, H - 6);
      const unit = strings.axis;
      ctx.fillText(unit, PAD_L + plotW / 2 - ctx.measureText(unit).width / 2, H - 6);
    };

    const frame = () => {
      if (stopped) return;
      draw();
      if (activeRef.current) raf = requestAnimationFrame(frame);
    };

    // Draw once, synchronously, before scheduling anything. After a scan the
    // loop is inactive, so the component gets exactly one animation frame — and
    // if that frame does not land before the canvas is looked at, the box is
    // simply blank. This is the moment the picture matters most, so it does not
    // get to depend on a frame arriving in time.
    draw();
    raf = requestAnimationFrame(frame);

    // The box changes size without the window changing size: switching views,
    // opening a drawer, a phone rotating. A canvas redraws for none of those on
    // its own, and a stale backing store at the wrong size reads as blank.
    const observer =
      typeof ResizeObserver === 'function' ? new ResizeObserver(() => draw()) : null;
    if (observer) observer.observe(canvas);

    const onResize = () => draw();
    window.addEventListener('resize', onResize);

    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      observer?.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, [active, getSpectrum, labelPeak, strings.bpm, strings.axis, t]);

  return <canvas ref={ref} className="floor" aria-label={t('floor.alt')} />;
}
